import { Children, cloneElement, createContext, createElement, Fragment, isValidElement, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { createPlatform, APPS } from "../../platform/core";
import { edition, pageOff } from "../../lib/extensions/registry";
import config from "../../../truechops.config";

// The platform (src/platform/core.js) in React: made once from truechops.config.js, its plugins set
// up in the browser, and the parts that read it re-drawn when a plugin changes something.
let made = null;
export function platform() { return made ||= createPlatform(config); }
const Context = createContext(null);

export function PlatformProvider({ children }) {
  const value = platform(), [version, setVersion] = useState(value.version());
  useEffect(() => value.subscribe(setVersion), [value]);
  // The site's colour and styles, as its plugins and settings have them (api.brand).
  useEffect(() => {
    const { accent, css } = value.branding(), root = document.documentElement;
    if (accent) root.style.setProperty("--tc-accent", accent); else root.style.removeProperty("--tc-accent");
    let style = document.getElementById("site-styles");
    if (css && !style) { style = document.createElement("style"); style.id = "site-styles"; document.head.appendChild(style); }
    if (style) style.textContent = css || "";
  }, [value, version]);
  // Pages put "TrueChops" in their titles. A site with its own name gets that instead, on all of them.
  useEffect(() => {
    const name = value.name;
    if (name === "TrueChops") return undefined;
    // (A name can have "TrueChops" in it: what's already that name stays as it is.)
    const swap = () => { const title = document.title.split(name).map((part) => part.replaceAll("TrueChops", name)).join(name); if (title !== document.title) document.title = title; };
    swap();
    const observer = new MutationObserver(swap);
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [value, version]);
  return createElement(Context.Provider, { value: { platform: value, version } }, children);
}
export function usePlatform() { return useContext(Context)?.platform || platform(); }
// Re-drawn whenever plugins change something.
export function usePlatformVersion() { return useContext(Context)?.version ?? 0; }

// A value as plugins have changed it (api.filter).
export function useFilter(name, value, context = {}) {
  const current = usePlatform(), version = usePlatformVersion();
  return useMemo(() => current.applyFilters(name, value, context), [current, version, name, value, JSON.stringify(context)]); // eslint-disable-line react-hooks/exhaustive-deps
}
export function useAppEnabled(id) { usePlatformVersion(); return usePlatform().appEnabled(id); }

// An insertion point: whatever plugins put here (api.slot), with `props`.
export function Slot({ name, ...props }) {
  const current = usePlatform();
  usePlatformVersion();
  const items = current.slotted(name);
  if (!items.length) return null;
  return createElement(Fragment, null, ...items.map(({ Component, props: own, plugin }, index) => createElement(Component, { key: `${plugin}:${index}`, ...own, ...props })));
}
// A part's words changed to the site's own, wherever they are in it, and what it tells screen readers.
function relabel(node, from, to) {
  if (typeof node === "string") return node === from ? to : node;
  if (!isValidElement(node)) return node;
  const change = {};
  if (node.props.children !== undefined) change.children = Children.map(node.props.children, (child) => relabel(child, from, to));
  if (typeof node.props["aria-label"] === "string") change["aria-label"] = to;
  if (typeof node.props.title === "string") change.title = to;
  return cloneElement(node, change);
}

const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;
// One of the site's named parts: shown as it is, taken out (api.hide), replaced (api.replace, which
// gets the original as `original` and the same props), renamed (api.rename: `label` says what its
// words are now) or moved (api.move: it's still made here, with all it knows, but drawn in another
// area of the page; when that area isn't on this page it stays here).
export function Part({ name, label, children, ...props }) {
  const current = usePlatform(), version = usePlatformVersion(), area = current.movedTo(name), [anchor, setAnchor] = useState(null);
  useBrowserLayoutEffect(() => { setAnchor(area ? document.querySelector(`[data-part-anchor="${name}"]`) : null); }, [area, name, version]);
  if (current.isHidden(name)) return null;
  const Replacement = current.replacement(name), words = current.label(name);
  const content = Replacement ? createElement(Replacement, { ...props, original: children }) : words != null && label ? relabel(children ?? null, label, words) : children ?? null;
  return anchor ? createPortal(content, anchor) : content;
}
// An area of named parts (src/platform/parts.js): its parts in the order the site chose (what isn't a
// part, like a divider, stays where it is), the parts moved here, and then whatever plugins put at
// `<name>.end`.
export function Area({ name, children, ...props }) {
  const current = usePlatform();
  usePlatformVersion();
  const items = Children.toArray(children), order = current.arrangement(name);
  const isPart = (item) => isValidElement(item) && item.type === Part;
  const rank = (item) => { const at = order.indexOf(item.props.name); return at < 0 ? order.length : at; };
  const parts = items.filter(isPart).map((item, index) => [item, index]).sort(([a, first], [b, second]) => rank(a) - rank(b) || first - second).map(([item]) => item);
  let next = 0;
  return createElement(Fragment, null, ...(order.length ? items.map((item) => isPart(item) ? parts[next++] : item) : items),
    ...current.movedInto(name).map((part) => createElement("span", { key: `moved:${part}`, "data-part-anchor": part, style: { display: "contents" } })),
    createElement(Slot, { name: `${name}.end`, ...props }));
}

// A page of an app the site has turned off: said plainly, with the way home.
export function AppGate({ pathname, query = {}, children }) {
  const current = usePlatform();
  usePlatformVersion();
  const app = current.appOfPath(pathname), off = pageOff(pathname, query);
  if (!off && (!app || current.appEnabled(app))) return children;
  const name = off?.name || APPS[app];
  return createElement("main", { style: { maxWidth: 560, margin: "120px auto", padding: "0 20px", textAlign: "center", fontFamily: "inherit" } },
    createElement("h1", { style: { fontSize: 24 } }, `${name || "This"} isn't on this site`),
    createElement("p", { style: { color: "#667085" } }, `${current.name} has turned ${name || "this app"} off.`),
    createElement(Link, { href: firstOpenApp(current) }, "Go to what's here"));
}
const HOMES = { score: "/", daw: "/daw", map: "/graph", reels: "/reels", community: "/community", grow: "/grow", studio: "/studio", modules: "/modules", practice: "/practice", vault: "/vault", byos: "/byos", room: "/room", tools: "/tools",
  store: "/store", extensions: "/extensions", school: "/school", lessons: "/lessons", marching: "/band" };
export const firstOpenApp = (current) => edition().home !== "/" ? edition().home : HOMES[Object.keys(HOMES).find((id) => current.appEnabled(id))] || "/";
